import { selMessage } from "@selector/shared";

import type WebApiClient from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type CoordinateList, type NuFeature } from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify.js";
import {
  DELETE_COORDINATE_LIST_FAILURE,
  DELETE_COORDINATE_LIST_REQUEST,
  DELETE_SAVED_ITEM_SUCCESS,
  LOAD_NUFEATURE_FAILURE,
  LOAD_NUFEATURE_REQUEST,
  LOAD_NUFEATURE_SUCCESS,
} from "@/redux/action-types.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

import { createReducer, type ReduxAction } from "../redux-utils.js";

/**
 * State definition
 */
export interface INuFeatureState {
  nuFeature?: NuFeature;
  isLoading: boolean;
}

const initialState: INuFeatureState = {
  nuFeature: undefined,
  isLoading: true,
};

/**
 * Reducers
 */
const nuFeatureReducer = createReducer(initialState, {
  [LOAD_NUFEATURE_REQUEST]: (state: INuFeatureState): INuFeatureState => {
    return { ...state, isLoading: true };
  },
  [LOAD_NUFEATURE_SUCCESS]: (state: INuFeatureState, payload: NuFeature): INuFeatureState => {
    return { ...state, isLoading: false, nuFeature: payload };
  },
  [LOAD_NUFEATURE_FAILURE]: (state: INuFeatureState): INuFeatureState => {
    return { ...state, isLoading: false, nuFeature: undefined };
  },
});

/**
 * Action creators
 */

// Last request in time unique identifier
let lastFetchedNuFeatureId = -1;

function loadNuFeature(nuFeatureId: number): ReduxAction<Promise<INuFeatureState>> {
  return (dispatch, getState, webApiClient) => {
    const state = getState();
    lastFetchedNuFeatureId = nuFeatureId;

    // fetch the gene only if no loading is currently in progress and gene is either absent or doesn't match the gene ID
    if (state.nuFeature.nuFeature?.id !== nuFeatureId) {
      dispatch({ type: LOAD_NUFEATURE_REQUEST });

      return webApiClient
        .fetchNuFeature(nuFeatureId)
        .then((nuFeature) => {
          if (lastFetchedNuFeatureId === nuFeature.id) {
            dispatch({
              type: LOAD_NUFEATURE_SUCCESS,
              payload: nuFeature,
            });
            return nuFeature;
          }

          return state.nuFeature.nuFeature;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_NUFEATURE_FAILURE,
            alertTitle: "Fetching of nuFeatures Failed",
          }),
        );
    } else {
      // We reset the loading flag to not stay on a loading state indefinitely
      // because lastFetchId can be not synchronized from state.gene.gene.id
      dispatch({
        type: LOAD_NUFEATURE_SUCCESS,
        payload: state.nuFeature.nuFeature,
      });

      return Promise.resolve(state.nuFeature.nuFeature);
    }
  };
}

export function deleteCoordinateLists(coordinateListIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<CoordinateList[]> => {
    dispatch({
      type: DELETE_COORDINATE_LIST_REQUEST,
      payload: coordinateListIds,
    });

    return webApiClient
      .deleteCoordinateListList(coordinateListIds)
      .then((response: boolean) => {
        dispatch({
          type: DELETE_SAVED_ITEM_SUCCESS,
          payload: {
            deletedIds: coordinateListIds,
            type: ENTITY_TYPE.COORD_LIST,
          },
        });

        notify.info(
          {
            title: selMessage({
              id: "coordinateList.deleted.successTitle",
              values: { coordinateListCount: coordinateListIds.length },
            }),
            children: selMessage({
              id: "coordinateList.deleted.successMessage",
              values: { coordinateListCount: coordinateListIds.length },
            }),
          },
          { autoClose: 10000 },
        );

        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_COORDINATE_LIST_FAILURE,
          alertTitle: selMessage({
            id: "coordinateList.deleted.errorTitle",
          }),
          alertMessage: selMessage({
            id: "coordinateList.deleted.errorMessage",
            values: { coordinateListCount: coordinateListIds.length },
          }),
          throwError: true, // to enter the rejected branch of the promise in case of errors
        }),
      );
  };
}

export { loadNuFeature, nuFeatureReducer };
